import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { notifyToast } from 'utils/responseUtils'
import { useDispatch } from 'react-redux'
import { Upload } from 'lucide-react'

const FileUpload = ({
  itemKey,
  isMobile,
  uploadFile,
  filesToAccept,
  isSingleFile,
  className = '',
  onDropExtraAction,
  fileInputRef,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [dragActive, setDragActive] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const uploadTimeoutRef = useRef(null)
  const lastUploadTime = useRef(0)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current)
      }
    }
  }, [])

  const handleChange = function (e, singleFile = false) {
    e.preventDefault()
    e.stopPropagation()

    // Prevent multiple rapid uploads (debouncing)
    const now = Date.now()
    if (now - lastUploadTime.current < 500 || isUploading) {
      console.log('FileUpload: Upload ignored due to debouncing or ongoing upload')
      return
    }
    lastUploadTime.current = now

    if (e.target.files && e.target.files.length > 0) {
      setIsUploading(true)

      // Clear any pending upload timeout
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current)
      }

      // Convert FileList to array for easier debugging
      const files = Array.from(e.target.files)
      console.log('FileUpload: handleChange called', {
        files: files.map((f) => ({ name: f.name, size: f.size })), // Log file details
        itemKey,
        singleFile,
      })

      // Call upload function
      if (uploadFile && typeof uploadFile === 'function') {
        try {
          uploadFile(files, itemKey, singleFile, e) // Pass array of files
        } catch (error) {
          console.error('FileUpload: Error during upload:', error)
          dispatch(notifyToast('error', t('common.upload-failed')))
        }
      } else {
        console.error('FileUpload: uploadFile is not a function:', uploadFile)
        dispatch(notifyToast('error', t('common.upload-failed')))
      }

      // Clear input after processing and reset uploading state
      e.target.value = ''

      // Reset uploading state after a delay
      setTimeout(() => {
        setIsUploading(false)
      }, 1000)
    } else {
      console.warn('FileUpload: No files selected in handleChange')
      setIsUploading(false)
    }
  }

  const handleDrop = function (e, singleFile = false) {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    // Prevent multiple rapid uploads (debouncing)
    const now = Date.now()
    if (now - lastUploadTime.current < 500 || isUploading) {
      console.log('FileUpload: Drop upload ignored due to debouncing or ongoing upload')
      return
    }
    lastUploadTime.current = now

    const { url } = getDraggedImageData(e)

    if (url && onDropExtraAction) {
      onDropExtraAction(url, itemKey, singleFile)
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setIsUploading(true)

      const files = Array.from(e.dataTransfer.files)
      console.log('FileUpload: handleDrop called', {
        files: files.map((f) => ({ name: f.name, size: f.size })), // Log file details
        itemKey,
        singleFile,
      })

      if (singleFile && files.length > 1) {
        dispatch(notifyToast('error', t('common.drop-single-file')))
        setIsUploading(false)
      } else {
        try {
          uploadFile(files, itemKey, singleFile, e) // Pass array of files
        } catch (error) {
          console.error('FileUpload: Error during drop upload:', error)
          dispatch(notifyToast('error', t('common.upload-failed')))
        }

        // Reset uploading state after a delay
        setTimeout(() => {
          setIsUploading(false)
        }, 1000)
      }
    } else {
      console.warn('FileUpload: No files dropped')
      setIsUploading(false)
    }
  }

  const handleDrag = function (e) {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const getDraggedImageData = (e) => {
    const draggedData = e.dataTransfer.getData('text/plain')
    try {
      return draggedData ? JSON.parse(draggedData) : {}
    } catch (error) {
      console.error('FileUpload: Error parsing dragged data:', error)
      return {}
    }
  }

  const handleLabelClick = (e) => {
    e.preventDefault()
    e.stopPropagation()

    // Prevent multiple rapid clicks
    const now = Date.now()
    if (now - lastUploadTime.current < 500 || isUploading) {
      console.log('FileUpload: Label click ignored due to debouncing or ongoing upload')
      return
    }

    // Trigger the file input
    if (fileInputRef?.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div
      className={`w-full relative flex items-center justify-center rounded-lg border border-dashed border-gray-300 transition-all duration-200 mb-3 ${
        dragActive ? 'bg-gray-50 border-blue-400' : 'bg-white hover:bg-gray-50'
      } h-32 ${className} ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
      onDragEnter={handleDrag}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <input
          ref={fileInputRef}
          type="file"
          id={`${itemKey}-file-upload`}
          onChange={(e) => handleChange(e, isSingleFile)}
          multiple={!isSingleFile}
          accept={filesToAccept}
          className="hidden"
        />
        <div onClick={handleLabelClick} className="cursor-pointer w-full h-full flex items-center justify-center">
          <div className="inline-flex flex-col gap-2 items-center">
            <Upload className="w-6 h-6 text-gray-400" />
            <p className="text-xs text-gray-500 font-medium text-center">
              {!isMobile && <span className="block mb-1">{t('model.drag-and-drop')}</span>}
              <span className="text-blue-500 font-semibold">
                {isUploading
                  ? t('common.uploading') || 'Uploading...'
                  : isMobile
                    ? t('model.upload-from-gallery')
                    : t('model.browse')}
              </span>
              <span className="block text-gray-400 text-[10px] mt-1 font-medium">{t('common.max-file-size')}</span>
            </p>
          </div>
        </div>
        {dragActive && (
          <div
            className="drag-container absolute z-10 w-full h-full rounded-lg top-0 right-0 bottom-0 left-0"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDrop(e, isSingleFile)}
          ></div>
        )}
      </form>
    </div>
  )
}

export default FileUpload
